import React, { useState } from "react";
import { adminAPI } from "../services/api";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  UploadCloud,
  Trash2,
  Plus,
  Save,
  Loader2,
  FileText,
  Image as ImageIcon,
  Type,
  DollarSign,
  Box,
  Grid,
  Tag,
  Key,
} from "lucide-react";

const AddProduct = () => {
  const [formData, setFormData] = useState({
    name: "",
    price: "",
    category: "",
    brand: "",
    description: "",
    externalId: "",
  });

  const [imageFile, setImageFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  const categories = [
    "men's clothing",
    "women's clothing",
    "electronics",
    "jewelery",
    "footwear",
    "accessories",
  ];

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setPreview(URL.createObjectURL(file));
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      setImageFile(file);
      setPreview(URL.createObjectURL(file));
    }
  };

  const removeImage = () => {
    setImageFile(null);
    setPreview(null);
  };

  const handleSubmit = async (e) => {
  e.preventDefault();

  try {
    const formDataToSend = new FormData();
    formDataToSend.append("externalId", formData.externalId);
    formDataToSend.append("name", formData.name);
    formDataToSend.append("price", formData.price);
    formDataToSend.append("category", formData.category);
    formDataToSend.append("brand", formData.brand);
    formDataToSend.append("description", formData.description);
    formDataToSend.append("countInStock", formData.countInStock);

    if (imageFile) formDataToSend.append("image", imageFile);

    setIsUploading(true);
    setUploadProgress(0);

    const toastId = toast.loading("Uploading product... 0%");

    const { data } = await adminAPI.addProduct(formDataToSend, {
      onUploadProgress: (progressEvent) => {
        const percent = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );
        setUploadProgress(percent);
        toast.update(toastId, {
          render: `Uploading product... ${percent}%`,
          isLoading: true,
        });
      },
    });

    toast.update(toastId, {
      render: data.message || "Product added successfully!",
      type: "success",
      isLoading: false,
      autoClose: 2000,
    });

    setFormData({
      name: "",
      price: "",
      category: "",
      brand: "",
      description: "",
      externalId: "",
      countInStock: "",
    });

    setImageFile(null);
    setPreview(null);
    setIsUploading(false);
    setUploadProgress(0);

  } catch (error) {
    console.error("Add Product Error:", error);
    setIsUploading(false);
    toast.error(error.response?.data?.message || "Failed to add product. Please try again.");
  }
};

  return (
    <div className="container py-4">
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop={true} closeOnClick={true} rtl={false} pauseOnFocusLoss={true} draggable={true} />
      
      {/* HEADER */}
      <div className="mb-4">
        <h2 className="fw-bold text-dark d-flex align-items-center gap-2 mb-1">
          <Plus size={28} className="text-success" />
          Add New Product
        </h2>
        <p className="text-muted small">Fill in the details below to add a new product to inventory</p>
      </div>

      <div className="row justify-content-center">
        <div className="col-xl-10 col-lg-15">
          <div className="card shadow-lg border-0 rounded-4" style={{borderTop: "4px solid #198754"}}>
            <div className="card-body p-5">

              <form onSubmit={handleSubmit}>
                <div className="row g-4">
                  {/* ROW 1: Product Name & Price */}
                  <div className="col-md-6">
                    <div className="mb-0">
                      <label className="form-label fw-bold d-flex align-items-center gap-2 mb-2">
                        <Type size={18} className="text-primary" />
                        Product Name
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className="form-control form-control-lg border-primary"
                        placeholder="Enter product name"
                        required
                      />
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="mb-0">
                      <label className="form-label fw-bold d-flex align-items-center gap-2 mb-2">
                        <DollarSign size={18} className="text-success" />
                        Price (₹)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        name="price"
                        value={formData.price}
                        onChange={handleChange}
                        className="form-control form-control-lg border-success"
                        placeholder="Enter price"
                        required
                      />
                    </div>
                  </div>

                  {/* ROW 2: Stock & Category */}
                  <div className="col-md-6">
                    <div className="mb-0">
                      <label className="form-label fw-bold d-flex align-items-center gap-2 mb-2 text-danger">
                        <Box size={18} />
                        Stock Quantity
                      </label>
                      <input
                        type="number"
                        name="countInStock"
                        value={formData.countInStock}
                        onChange={handleChange}
                        className="form-control form-control-lg border-danger"
                        placeholder="Enter available stock"
                        min={0}
                        required
                      />
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="mb-0">
                      <label className="form-label fw-bold d-flex align-items-center gap-2 mb-2">
                        <Grid size={18} className="text-primary" />
                        Category
                      </label>
                      <select
                        name="category"
                        className="form-select form-select-lg border-primary"
                        value={formData.category}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select Category</option>
                        {categories.map((cat, index) => (
                          <option key={index} value={cat}>
                            {cat}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* ROW 3: Brand & External ID */}
                  <div className="col-md-6">
                    <div className="mb-0">
                      <label className="form-label fw-bold d-flex align-items-center gap-2 mb-2">
                        <Tag size={18} className="text-primary" />
                        Brand
                      </label>
                      <input
                        type="text"
                        name="brand"
                        value={formData.brand}
                        onChange={handleChange}
                        className="form-control form-control-lg border-primary"
                        placeholder="Enter brand"
                        required
                      />
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="mb-0">
                      <label className="form-label fw-bold d-flex align-items-center gap-2 mb-2">
                        <Key size={18} className="text-primary" />
                        External ID
                      </label>
                      <input
                        type="text"
                        name="externalId"
                        value={formData.externalId}
                        onChange={handleChange}
                        className="form-control form-control-lg border-primary"
                        placeholder="Unique external ID"
                        required
                      />
                    </div>
                  </div>

                  {/* ROW 4: Image Upload (Full Width) */}
                  <div className="col-12">
                    <div
                      className={`border-3 rounded-4 p-4 text-center mb-4 position-relative transition-all ${
                        isDragging
                          ? "bg-success bg-opacity-10 border-success shadow-lg"
                          : "bg-light border-secondary border-2"
                      }`}
                      style={{cursor: "pointer", minHeight: "300px", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center"}}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                    >
                      {!preview ? (
                        <>
                          <UploadCloud size={48} className={`mb-3 ${isDragging ? "text-success" : "text-primary"}`} />
                          <p className="fw-semibold mb-2">Drag & drop image here</p>
                          <p className="text-muted small mb-3">or click button below</p>
                          <label className="btn btn-outline-primary btn-sm px-4 d-flex align-items-center gap-2" style={{cursor: "pointer"}}>
                            <ImageIcon size={16} />
                            Choose Image
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleFileChange}
                              className="d-none"
                            />
                          </label>
                        </>
                      ) : (
                        <div className="position-relative d-inline-block">
                          <img
                            src={preview}
                            alt="Preview"
                            className="rounded-3"
                            style={{
                              maxWidth: "100%",
                              maxHeight: "280px",
                              objectFit: "cover",
                              boxShadow: "0 4px 10px rgba(0,0,0,0.1)"
                            }}
                          />
                          <button
                            type="button"
                            className="btn btn-danger btn-sm position-absolute top-0 end-0 m-2 rounded-circle shadow-sm d-flex align-items-center justify-content-center"
                            onClick={removeImage}
                            style={{
                              width: "36px",
                              height: "36px",
                            }}
                          >
                            <Trash2 size={16} />
                          </button>
                          <label className="btn btn-sm btn-outline-primary position-absolute bottom-0 start-0 m-2 d-flex align-items-center gap-1">
                            <ImageIcon size={14} />
                            <span className="d-none d-sm-inline">Change</span>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleFileChange}
                              className="d-none"
                            />
                          </label>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* ROW 5: Description (Full Width) */}
                  <div className="col-12">
                    <div className="mb-0">
                      <label className="form-label fw-bold d-flex align-items-center gap-2 mb-2">
                        <FileText size={18} className="text-primary" />
                        Description
                      </label>
                      <textarea
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        rows={4}
                        className="form-control form-control-lg border-primary"
                        placeholder="Enter product description"
                      />
                    </div>
                  </div>
                </div>
                <div className="text-end mt-5 pt-3 border-top">
                  <button
                    type="submit"
                    className="btn btn-success btn-lg fw-bold px-5 d-inline-flex align-items-center gap-2 shadow-sm"
                    disabled={isUploading}
                    style={{fontSize: "16px"}}
                  >
                    {isUploading ? (
                      <>
                        <Loader2 size={18} style={{animation: "spin 1s linear infinite"}} />
                        Uploading... {uploadProgress}%
                      </>
                    ) : (
                      <>
                        <Save size={18} />
                        Add Product
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddProduct;
